import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../services/authService';

// Async thunks for authentication actions
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.getCurrentUser();
      if (user) {
        // Only get attributes if user is authenticated
        try {
          const attributes = await authService.getUserAttributes();
          return {
            user,
            attributes,
            cognitoSub: authService.getSub(),
            isAuthenticated: true
          };
        } catch (error) {
          // If getting attributes fails, still return user but without attributes
          console.warn('Failed to get user attributes:', error);
          return {
            user,
            attributes: null,
            cognitoSub: authService.getSub(),
            isAuthenticated: true
          };
        }
      }
      return {
        user: null,
        attributes: null,
        cognitoSub: null,
        isAuthenticated: false
      };
    } catch (error) {
      // User is not authenticated
      return {
        user: null,
        attributes: null,
        isAuthenticated: false
      };
    }
  }
);

export const signIn = createAsyncThunk(
  'auth/signIn',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const result = await authService.signIn(username, password);
      if (result.success) {
        // Try to get user attributes, but continue if it fails
        let attributes = null;
        try {
          attributes = await authService.getUserAttributes();
        } catch (attrError) {
          console.warn('Could not get user attributes after sign in:', attrError);
          attributes = result.user?.attributes || null;
        }

        return {
          user: result.user,
          attributes,
          cognitoSub: authService.getSub(),
          isAuthenticated: true
        };
      }
      return rejectWithValue(result.error || 'Sign in failed');
    } catch (error) {
      console.log('SignIn catch error:', error);
      // Use authService to format the error message consistently
      const formattedError = authService.formatErrorMessage(error);
      return rejectWithValue(formattedError);
    }
  }
);

export const tokenSignIn = createAsyncThunk(
  'auth/tokenSignIn',
  async (idToken, { rejectWithValue }) => {
    try {
      const user = await authService.signInWithToken(idToken);
      return {
        user,
        attributes: user.attributes,
        cognitoSub: user.attributes?.sub || null,
        isAuthenticated: true
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const signOut = createAsyncThunk(
  'auth/signOut',
  async (_, { rejectWithValue }) => {
    try {
      const result = await authService.signOut();
      if (result.success) {
        return {
          user: null,
          attributes: null,
          isAuthenticated: false
        };
      }
      return rejectWithValue(result.error || 'Sign out failed');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const signUp = createAsyncThunk(
  'auth/signUp',
  async ({ username, password, email, attributes }, { rejectWithValue }) => {
    try {
      const result = await authService.signUp(username, password, email, attributes);
      if (result.success) {
        return {
          user: result.user,
          userSub: result.userSub,
          requiresConfirmation: result.requiresConfirmation || false,
          username: username // Ensure we have the username for confirmation
        };
      }
      console.log('SignUp error from authService:', result.error);
      return rejectWithValue(result.error || 'Sign up failed');
    } catch (error) {
      console.log('SignUp catch error:', error);
      const formattedError = authService.formatErrorMessage(error);
      return rejectWithValue(formattedError);
    }
  }
);

export const confirmSignUp = createAsyncThunk(
  'auth/confirmSignUp',
  async ({ username, code }, { rejectWithValue }) => {
    try {
      const result = await authService.confirmSignUp(username, code);
      if (result.success) {
        return { confirmed: true };
      }
      console.log('ConfirmSignUp error from authService:', result.error);
      return rejectWithValue(result.error || 'Confirmation failed');
    } catch (error) {
      console.log('ConfirmSignUp catch error:', error);
      const formattedError = authService.formatErrorMessage(error);
      return rejectWithValue(formattedError);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ username }, { rejectWithValue }) => {
    try {
      const result = await authService.forgotPassword(username);
      if (result.success) {
        return { passwordResetSent: true };
      }
      console.log('ForgotPassword error from authService:', result.error);
      return rejectWithValue(result.error || 'Password reset failed');
    } catch (error) {
      console.log('ForgotPassword catch error:', error);
      const formattedError = authService.formatErrorMessage(error);
      return rejectWithValue(formattedError);
    }
  }
);

export const forgotPasswordSubmit = createAsyncThunk(
  'auth/forgotPasswordSubmit',
  async ({ username, code, newPassword }, { rejectWithValue }) => {
    try {
      const result = await authService.forgotPasswordSubmit(username, code, newPassword);
      if (result.success) {
        return { passwordReset: true };
      }
      console.log('ForgotPasswordSubmit error from authService:', result.error);
      return rejectWithValue(result.error || 'Password reset confirmation failed');
    } catch (error) {
      console.log('ForgotPasswordSubmit catch error:', error);
      const formattedError = authService.formatErrorMessage(error);
      return rejectWithValue(formattedError);
    }
  }
);

export const signInWithAzure = createAsyncThunk(
  'auth/signInWithAzure',
  async (_, { rejectWithValue }) => {
    try {
      const result = await authService.signInWithAzure();
      if (result.success) {
        if (result.redirecting) {
          // OAuth redirect initiated - return special state
          return {
            redirecting: true,
            isAuthenticated: false
          };
        }
        // If successful without redirect (shouldn't happen with OAuth)
        let attributes = null;
        try {
          attributes = await authService.getUserAttributes();
        } catch (attrError) {
          console.warn('Could not get user attributes after Azure sign in:', attrError);
          attributes = result.user?.attributes || null;
        }

        return {
          user: result.user,
          attributes,
          cognitoSub: authService.getSub(),
          isAuthenticated: true
        };
      }
      return rejectWithValue(result.error || 'Azure sign in failed');
    } catch (error) {
      console.log('SignInWithAzure catch error:', error);
      // Use authService to format the error message consistently
      const formattedError = authService.formatErrorMessage(error);
      return rejectWithValue(formattedError);
    }
  }
);

export const handleOAuthCallback = createAsyncThunk(
  'auth/handleOAuthCallback',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const result = await authService.handleOAuthCallback();
      if (result.success) {
        let attributes = {};
        try {
          attributes = await authService.getUserAttributes();
        } catch (attrError) {
          console.warn('Could not get user attributes:', attrError);
          // Use attributes from token if available
          attributes = result.user?.attributes || {};
        }
        
        return {
          user: result.user,
          attributes,
          cognitoSub: authService.getSub(),
          isAuthenticated: true
        };
      }

      // If retry is requested, try again after a delay
      if (result.retry) {
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('OAuth callback needs retry, waiting...');
        }
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Retry once with the stored authorization code
        const retryResult = await authService.handleOAuthCallback(result.code);
        if (retryResult.success) {
          let attributes = {};
          try {
            attributes = await authService.getUserAttributes();
          } catch (attrError) {
            console.warn('Could not get user attributes on retry:', attrError);
            attributes = retryResult.user?.attributes || {};
          }

          return {
            user: retryResult.user,
            attributes,
            cognitoSub: authService.getSub(),
            isAuthenticated: true
          };
        }
      }
      
      return rejectWithValue(result.error || 'OAuth callback failed');
    } catch (error) {
      console.log('handleOAuthCallback catch error:', error);
      const formattedError = authService.formatErrorMessage(error);
      return rejectWithValue(formattedError);
    }
  }
);

// Initial state
const initialState = {
  user: null,
  attributes: null,
  cognitoSub: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  
  // UI state for different flows
  showSignIn: false,
  showSignUp: false,
  showForgotPassword: false,
  showConfirmSignUp: false,
  showContact: false,
  
  // Flow state
  requiresConfirmation: false,
  passwordResetSent: false,
  passwordReset: false,
  pendingUsername: null, // For confirmation flows
  successMessage: null, // For displaying success messages
  
  // Status
  status: 'idle' // 'idle', 'loading', 'succeeded', 'failed'
};

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // UI actions
    showSignInModal: (state) => {
      state.showSignIn = true;
      state.showSignUp = false;
      state.showForgotPassword = false;
      state.showConfirmSignUp = false;
      state.showContact = false;
      // Don't clear error - let it persist to show validation errors
    },
    showSignUpModal: (state) => {
      state.showSignUp = true;
      state.showSignIn = false;
      state.showForgotPassword = false;
      state.showConfirmSignUp = false;
      state.showContact = false;
      // Don't clear error - let it persist to show validation errors
    },
    showForgotPasswordModal: (state) => {
      state.showForgotPassword = true;
      state.showSignIn = false;
      state.showSignUp = false;
      state.showConfirmSignUp = false;
      state.showContact = false;
      // Don't clear error - let it persist to show validation errors
    },
    showConfirmSignUpModal: (state, action) => {
      state.showConfirmSignUp = true;
      state.showSignIn = false;
      state.showSignUp = false;
      state.showForgotPassword = false;
      state.showContact = false;
      state.pendingUsername = action.payload?.username || null;
      // Don't clear error - let it persist to show validation errors
    },
    showContactModal: (state) => {
      state.showContact = true;
      state.showSignIn = false;
      state.showSignUp = false;
      state.showForgotPassword = false;
      state.showConfirmSignUp = false;
      // Don't clear error - let it persist to show validation errors
    },
    hideAuthModals: (state) => {
      console.log('hideAuthModals called!', new Error().stack);
      state.showSignIn = false;
      state.showSignUp = false;
      state.showForgotPassword = false;
      state.showConfirmSignUp = false;
      state.showContact = false;
      state.error = null;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    
    // Reset flow state
    resetFlowState: (state) => {
      state.requiresConfirmation = false;
      state.passwordResetSent = false;
      state.passwordReset = false;
      state.pendingUsername = null;
      state.error = null;
    },
    
    // Set pending username for confirmation
    setPendingUsername: (state, action) => {
      state.pendingUsername = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Initialize auth
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
        state.status = 'loading';
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.attributes = action.payload.attributes;
        state.cognitoSub = action.payload.cognitoSub || null;
        state.isAuthenticated = action.payload.isAuthenticated;
        state.error = null;
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.status = 'failed';
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.attributes = null;
      });
    
    // Sign in
    builder
      .addCase(signIn.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.attributes = action.payload.attributes;
        state.cognitoSub = action.payload.cognitoSub || null;
        state.isAuthenticated = action.payload.isAuthenticated;
        state.error = null;
        state.showSignIn = false;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // Token sign in (pre-obtained Cognito ID token, e.g. from native app guest flow)
    builder
      .addCase(tokenSignIn.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(tokenSignIn.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.attributes = action.payload.attributes;
        state.cognitoSub = action.payload.cognitoSub || null;
        state.isAuthenticated = action.payload.isAuthenticated;
        state.error = null;
        state.showSignIn = false;
      })
      .addCase(tokenSignIn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // Sign out
    builder
      .addCase(signOut.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.attributes = null;
        state.cognitoSub = null;
        state.isAuthenticated = false;
        state.error = null;
        // Don't show sign in modal after sign out - user should see LandingPage
        // and must explicitly click "Try it now" to sign in again
      })
      .addCase(signOut.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    
    // Sign up
    builder
      .addCase(signUp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        console.log('SignUp fulfilled - authSlice DEBUG:', {
          payload: action.payload,
          requiresConfirmation: action.payload.requiresConfirmation,
          username: action.payload.user?.username || action.payload.username
        });
        
        state.isLoading = false;
        state.requiresConfirmation = action.payload.requiresConfirmation;
        state.pendingUsername = action.payload.user?.username || action.payload.username;
        state.error = null;
        
        if (action.payload.requiresConfirmation) {
          console.log('Setting showConfirmSignUp = true');
          state.showSignUp = false;
          state.showConfirmSignUp = true;
        } else {
          console.log('Setting showSignIn = true (no confirmation needed)');
          // If no confirmation needed, user is signed up and can sign in
          state.showSignUp = false;
          state.showSignIn = true;
          state.successMessage = 'Account created successfully! You can now sign in.';
        }
      })
      .addCase(signUp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    
    // Confirm sign up
    builder
      .addCase(confirmSignUp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmSignUp.fulfilled, (state) => {
        state.isLoading = false;
        state.requiresConfirmation = false;
        state.showConfirmSignUp = false;
        state.showSignIn = true;
        state.error = null;
        state.successMessage = 'Email verified successfully! You can now sign in to your account.';
      })
      .addCase(confirmSignUp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    
    // Forgot password
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.passwordResetSent = action.payload.passwordResetSent;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    
    // Forgot password submit
    builder
      .addCase(forgotPasswordSubmit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPasswordSubmit.fulfilled, (state) => {
        state.isLoading = false;
        state.passwordReset = true;
        state.passwordResetSent = false;
        state.showForgotPassword = false;
        state.showSignIn = true;
        state.error = null;
        state.successMessage = 'Password reset successfully. You can now sign in with your new password.';
      })
      .addCase(forgotPasswordSubmit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    
    // Sign in with Azure
    builder
      .addCase(signInWithAzure.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signInWithAzure.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.redirecting) {
          // OAuth redirect initiated
          state.isAuthenticated = false;
          // Do not change user or attributes - they are not yet available
        } else {
          // Sign in successful without redirect (shouldn't happen with OAuth)
          state.user = action.payload.user;
          state.attributes = action.payload.attributes;
          state.cognitoSub = action.payload.cognitoSub || null;
          state.isAuthenticated = true;
        }
        state.error = null;
      })
      .addCase(signInWithAzure.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });
    
    // Handle OAuth callback
    builder
      .addCase(handleOAuthCallback.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(handleOAuthCallback.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.attributes = action.payload.attributes;
        state.cognitoSub = action.payload.cognitoSub || null;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(handleOAuthCallback.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });
  }
});

// Export actions
export const {
  showSignInModal,
  showSignUpModal,
  showForgotPasswordModal,
  showConfirmSignUpModal,
  showContactModal,
  hideAuthModals,
  clearError,
  clearSuccessMessage,
  resetFlowState,
  setPendingUsername
} = authSlice.actions;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectUserAttributes = (state) => state.auth.attributes;
export const selectAuthLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;
export const selectShowAuthModals = (state) => ({
  showSignIn: state.auth.showSignIn,
  showSignUp: state.auth.showSignUp,
  showForgotPassword: state.auth.showForgotPassword,
  showConfirmSignUp: state.auth.showConfirmSignUp
});

// Export reducer
export default authSlice.reducer;
